import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { MdOutlineEdit } from "react-icons/md";
import { useEffect, useState } from "react";
import BluePrimaryButton from "../../common/button/bluePrimaryButton";
import { useDispatch, useSelector } from "react-redux";
import customerActions from "../../redux/customer/actions";
import ReconcilationReportTable from "../../common/ReconcilationReportTable";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend } from "chart.js";
import masterActions from "../../redux/master/actions";

ChartJS.register(ArcElement, ChartTooltip, Legend);

// ── Filter option lists ────────────────────────────────────────────────────────

const RECONCILIATION_STATUS_OPTIONS = [
  { value: "",                  label: "All"               },
  { value: "RECONCILED",        label: "Reconciled"        },
  { value: "STATUS_MISMATCH",   label: "Status Mismatch"},
  { value: "AMOUNT_MISMATCH",   label: "Amount Mismatch"   },
  { value: "MISSING_IN_AIRPAY",  label: "Missing in Airpay"  },
  { value: "MISSING_IN_PORTAL", label: "Missing in Portal"  },
  { value: "PENDING",           label: "Pending"            },
  { value: "DELAYED",           label: "Delayed"           },
  { value: "DUPLICATE",         label: "Duplicate"         },
  { value: "MANUAL",            label: "Manual Override"   },
  { value: "UNCLASSIFIED",      label: "Unclassified"      },
];

const PORTAL_STATUS_OPTIONS = [
  { value: "",        label: "All"     },
  { value: "SUCCESS", label: "Success" },
  { value: "FAILED",  label: "Failed"  },
  { value: "PENDING", label: "Pending" },
];

const AIRPAY_STATUS_OPTIONS = [
  { value: "",          label: "All"       },
  { value: "SUCCESS",   label: "Success"   },
  { value: "FAILED",    label: "Failed"    },
  { value: "SALE_VOID", label: "Sale Void" },
  { value: "REFUNDED",  label: "Refunded"  },
];

// ── Color map ──────────────────────────────────────────────────────────────────

const STATUS_CHIP_SX = {
  RECONCILED:         { backgroundColor: "#e8f5e9", color: "#2e7d32" },
  STATUS_MISMATCH:    { backgroundColor: "#ffebee", color: "#c62828" },
  AMOUNT_MISMATCH:    { backgroundColor: "#ffebee", color: "#c62828" },
  MISSING_IN_AIRPAY:  { backgroundColor: "#fce4ec", color: "#b71c1c" },
  MISSING_IN_PORTAL:  { backgroundColor: "#e3f2fd", color: "#1565c0" },
  PENDING:            { backgroundColor: "#fffde7", color: "#f57f17" },
  DELAYED:            { backgroundColor: "#fff3e0", color: "#e65100" },
  DUPLICATE:          { backgroundColor: "#f3e5f5", color: "#6a1b9a" },
  MANUAL:             { backgroundColor: "#e8eaf6", color: "#283593" },
  UNCLASSIFIED:       { backgroundColor: "#f5f5f5", color: "#616161" },
};

// Summary card background colours (lighter than chip)
const STATUS_CARD_BG = {
  RECONCILED:        "#e8f5e9",
  STATUS_MISMATCH:   "#ffebee",
  AMOUNT_MISMATCH:   "#ffebee",
  MISSING_IN_AIRPAY: "#fce4ec",
  MISSING_IN_PORTAL: "#e3f2fd",
  PENDING:           "#fffde7",
  DELAYED:           "#fff3e0",
  DUPLICATE:         "#f3e5f5",
  MANUAL:            "#e8eaf6",
  TOTAL:             "#f0f4ff",
};

// Pie chart colours (same order as SUMMARY_KEYS below)
const PIE_COLORS = [
  "#43a047", "#e53935", "#fb8c00", "#f44336",
  "#1e88e5", "#ffd600", "#ff6d00", "#8e24aa", "#3949ab",
];

const SUMMARY_KEYS = [
  "RECONCILED", "STATUS_MISMATCH", "AMOUNT_MISMATCH", "MISSING_IN_AIRPAY",
  "MISSING_IN_PORTAL", "PENDING", "DELAYED", "DUPLICATE", "MANUAL",
];

// ── Column definitions ─────────────────────────────────────────────────────────

const buildColumns = (onOverride) => [
  { name: "Sr No",          dataIndex: "srNo"              },
  { name: "Transaction ID", dataIndex: "transaction_id"    },
  { name: "Candidate ID",   dataIndex: "candidateid"       },
  { name: "Course",         dataIndex: "coursename"        },
  { name: "Email",          dataIndex: "emailid"           },
  { name: "Calc. Amount",   dataIndex: "calculated_total"  },
  { name: "Net Paid",       dataIndex: "net_paid_amount"   },
  { name: "Difference",     dataIndex: "difference_amount" },
  { name: "Portal Status",  dataIndex: "portal_status"     },
  { name: "Airpay Status",  dataIndex: "airpay_status"     },
  { name: "Airpay ID",      dataIndex: "airpay_id"         },
  {
    name: "Payment Date",
    dataIndex: "paymentdate",
    render: (val) =>
      val && val !== "NA"
        ? new Date(val).toLocaleDateString("en-IN")
        : "-",
  },
  {
    name: "Reconciliation Status",
    dataIndex: "reconciliation_status",
    render: (val) =>
      val && val !== "NA" ? (
        <Chip
          label={val.replace(/_/g, " ")}
          size="small"
          sx={{
            ...(STATUS_CHIP_SX[val] ?? STATUS_CHIP_SX.UNCLASSIFIED),
            fontWeight: 600,
            fontSize: "0.7rem",
          }}
        />
      ) : (
        "-"
      ),
  },
  // {
  //   name: "Action",
  //   dataIndex: "_action",
  //   render: (_, row) => {
  //     const isMissingInPortal = row.reconciliation_status === "MISSING_IN_PORTAL";
  //     return (
  //       <Tooltip title={isMissingInPortal ? "No portal record — override not available" : "Manual Override"}>
  //         <span>
  //           <IconButton
  //             size="small"
  //             onClick={() => onOverride(row)}
  //             disabled={isMissingInPortal}
  //             sx={{ color: isMissingInPortal ? "#bdbdbd" : "#3949ab" }}
  //           >
  //             <MdOutlineEdit size={18} />
  //           </IconButton>
  //         </span>
  //       </Tooltip>
  //     );
  //   },
  // },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

const toInputValue = (stored) =>
  stored ? stored.slice(0, 16).replace(" ", "T") : "";

const fromInputValue = (val) => (val ? val.replace("T", " ") + ":00" : "");

// ── Sub-components ─────────────────────────────────────────────────────────────

function SummaryCards({ summary }) {
  const total = SUMMARY_KEYS.reduce((acc, k) => acc + (Number(summary[k]) || 0), 0);
  if (total === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
        Summary
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={1.5}>
        <Box
          sx={{
            minWidth: 90,
            p: 1.5,
            borderRadius: 2,
            backgroundColor: STATUS_CARD_BG.TOTAL,
            textAlign: "center",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          <Typography variant="h6" fontWeight={700}>{total}</Typography>
          <Typography variant="caption" color="textSecondary">Total</Typography>
        </Box>

        {SUMMARY_KEYS.map((key) => {
          const count = Number(summary[key]) || 0;
          const sx    = STATUS_CHIP_SX[key] ?? STATUS_CHIP_SX.UNCLASSIFIED;
          return (
            <Box
              key={key}
              sx={{
                minWidth: 110,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: STATUS_CARD_BG[key] ?? "#f5f5f5",
                textAlign: "center",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ color: sx.color }}>
                {count}
              </Typography>
              <Typography variant="caption" sx={{ color: sx.color }}>
                {key.replace(/_/g, " ")}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function StatusPieChart({ summary }) {
  const values = SUMMARY_KEYS.map((k) => Number(summary[k]) || 0);
  const total  = values.reduce((a, b) => a + b, 0);
  if (total === 0) return null;

  const data = {
    labels: SUMMARY_KEYS.map((k) => k.replace(/_/g, " ")),
    datasets: [
      {
        data: values,
        backgroundColor: PIE_COLORS,
        borderWidth: 1,
      },
    ],
  };

  const options = {
    plugins: {
      legend: { position: "right", labels: { font: { size: 11 } } },
    },
    maintainAspectRatio: false,
  };

  return (
    <Box sx={{ height: 220, mb: 3 }}>
      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
        Status Distribution
      </Typography>
      <Pie data={data} options={options} />
    </Box>
  );
}

function OverrideModal({ open, row, onClose, onSubmit }) {
  const [remarks, setRemarks] = useState("");

  const handleSubmit = () => {
    if (!remarks.trim()) return;
    onSubmit(row, remarks.trim());
    setRemarks("");
    onClose();
  };

  const handleClose = () => { setRemarks(""); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manual Override</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2">
            <b>Candidate ID:</b> {row?.candidateid ?? "-"}
          </Typography>
          <Typography variant="body2">
            <b>Course:</b> {row?.coursename ?? "-"}
          </Typography>
          <Typography variant="body2">
            <b>Current Status:</b>{" "}
            {row?.reconciliation_status ? (
              <Chip
                label={row.reconciliation_status.replace(/_/g, " ")}
                size="small"
                sx={{
                  ...(STATUS_CHIP_SX[row.reconciliation_status] ?? STATUS_CHIP_SX.UNCLASSIFIED),
                  fontWeight: 600,
                  fontSize: "0.7rem",
                }}
              />
            ) : (
              "-"
            )}
          </Typography>
          <TextField
            label="Remarks *"
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter reason for manual override..."
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!remarks.trim()}
          sx={{ backgroundColor: "#3949ab" }}
        >
          Override to MANUAL
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function Index() {
  const dispatch = useDispatch();

  // Step 1 – upload
  const [file,  setFile]  = useState(null);
  const [error, setError] = useState(null);

  // Step 2 – sync date range
  const [fromDate, setFromDate] = useState("");
  const [toDate,   setToDate]   = useState("");

  // Step 3 – report date range (independent from Step 2)
  const [reportFromDate, setReportFromDate] = useState("");
  const [reportToDate,   setReportToDate]   = useState("");

  // Step 3 – filters
  const [reconciliationStatus, setReconciliationStatus] = useState("");
  const [portalStatus,         setPortalStatus]         = useState("");
  const [airpayStatus,         setAirpayStatus]         = useState("");

  // Pagination (server-side, 1-based for API)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize,    setPageSize]    = useState(50);

  // Override modal
  const [overrideRow,  setOverrideRow]  = useState(null);
  const [overrideOpen, setOverrideOpen] = useState(false);

  // Redux
  const { reconciliationReport } = useSelector((state) => state.customerReducer);
  const tableRows  = reconciliationReport?.result     || [];
  const pagination = reconciliationReport?.pagination || {};
  const summary    = reconciliationReport?.summary    || {};

  const { courseList } = useSelector((state) => state.masterReducer);

  useEffect(() => {
    dispatch({ type: masterActions.GET_COURSE_LIST });
  }, [dispatch]);

  const [courseid, setCourseId] = useState("");
   const onChangeCourse = (e) => {
    setCourseId(e.target.value);
  };

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleFileChange = (e) => { setFile(e.target.files[0]); setError(null); };

  const handleUpload = () => {
    if (!file) { setError("Please select a CSV file"); return; }
    setError(null);
    const formData = new FormData();
    formData.append("airpay-report", file);
    dispatch({ type: customerActions.UPLOAD_RECONCILIATION_FILE, payload: formData });
  };

  const handleFromDateChange       = (e) => setFromDate(fromInputValue(e.target.value));
  const handleToDateChange         = (e) => setToDate(fromInputValue(e.target.value));
  const handleReportFromDateChange = (e) => setReportFromDate(fromInputValue(e.target.value));
  const handleReportToDateChange   = (e) => setReportToDate(fromInputValue(e.target.value));

  const dispatchReport = (page, limit) => {
    dispatch({
      type: customerActions.GET_RECONCILIATION_REPORT,
      payload: {
        fromDate: reportFromDate,
        toDate:   reportToDate,
        reconciliationStatus: reconciliationStatus || undefined,
        portalStatus:         portalStatus         || undefined,
        airpayStatus:         airpayStatus         || undefined,
        courseid:             courseid             || undefined,
        page,
        limit,
      },
    });
  };
  console.log("courseid",courseid);

  const handleGenerate = () => {
    setCurrentPage(1);
    dispatchReport(1, pageSize);
  };

  const handlePageChange = (_, newPage) => {
    const p = newPage + 1;
    setCurrentPage(p);
    dispatchReport(p, pageSize);
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPageSize(newLimit);
    setCurrentPage(1);
    dispatchReport(1, newLimit);
  };

  // Override handlers
  const handleOverrideOpen = (row) => { setOverrideRow(row); setOverrideOpen(true); };
  const handleOverrideClose = () => { setOverrideOpen(false); setOverrideRow(null); };

  const handleOverrideSubmit = (row, remarks) => {
    dispatch({
      type: customerActions.OVERRIDE_RECONCILIATION_STATUS,
      payload: {
        candidateid:       row.candidateid,
        courseid:          row.courseid,
        transaction_id:    row.transaction_id !== "NA" ? row.transaction_id : null,
        emailid:           row.emailid,
        original_status:   row.reconciliation_status,
        override_status:   "MANUAL",
        remarks,
        overridden_by:     "backoffice",
      },
      onSuccess: () => dispatchReport(currentPage, pageSize),
    });
  };

  // ── Table data mapping ───────────────────────────────────────────────────────

  const applicationStatusData = tableRows.map((item, index) => ({
    srNo:                  (currentPage - 1) * pageSize + index + 1,
    transaction_id:        item.transaction_id          ?? "NA",
    candidateid:           item.candidateid             ?? "NA",
    courseid:              item.courseid                ?? null,
    coursename:            item.coursename              ?? "NA",
    emailid:               item.emailid                 ?? "NA",
    calculated_total:      Number(item.calculated_total)  || 0,
    net_paid_amount:       Number(item.net_paid_amount)   || 0,
    difference_amount:     Number(item.difference_amount) || 0,
    portal_status:         item.portal_status           ?? "NA",
    airpay_status:         item.airpay_status           ?? "NA",
    airpay_id:             item.airpay_id               ?? "NA",
    paymentdate:           item.paymentdate             ?? "NA",
    reconciliation_status: item.reconciliation_status   ?? "NA",
  }));

  const reconciliationColumns = buildColumns(handleOverrideOpen);

  // ── CSV download ─────────────────────────────────────────────────────────────

  const downloadReport = () => {
    dispatch({
      type: customerActions.DOWNLOAD_RECONCILIATION_REPORT,
      payload: {
        fromDate: reportFromDate,
        toDate:   reportToDate,
        reconciliationStatus: reconciliationStatus || undefined,
        portalStatus:         portalStatus         || undefined,
        airpayStatus:         airpayStatus         || undefined,
        courseid:             courseid             || undefined,
      },
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        margin: "1rem auto",
        backgroundColor: "rgba(255, 255, 255, 1)",
        borderRadius: "8px",
        padding: "10px",
      }}
    >
      <Card sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Reconciliation Report</Typography>
        </Box>

        {/* ── Step 1 – Upload Airpay CSV ─────────────────────────────────── */}
        <Grid item sm={12}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary">
              <b>Step 1</b> – Upload Airpay CSV file
            </Typography>
            <Box display="flex" gap={2} mt={2} alignItems="center" flexWrap="wrap">
              <TextField
                type="file"
                inputProps={{ accept: ".csv" }}
                onChange={handleFileChange}
                sx={{ width: "280px" }}
                size="small"
              />
              <Button variant="contained" onClick={handleUpload} disabled={!file}>
                Upload
              </Button>
            </Box>
            {error && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>
          <hr style={{ margin: "20px 0", borderColor: "lightgray" }} />
        </Grid>

        {/* ── Step 2 – Sync latest DB dump ──────────────────────────────── */}
        <Grid item sm={12}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary">
              <b>Step 2</b> – Sync latest DB dump for accurate reconciliation
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" mt={2} mb={2}>
              <TextField
                label="From Date"
                type="datetime-local"
                value={toInputValue(fromDate)}
                onChange={handleFromDateChange}
                sx={{ width: "260px" }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To Date"
                type="datetime-local"
                value={toInputValue(toDate)}
                onChange={handleToDateChange}
                sx={{ width: "260px" }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <BluePrimaryButton
              title="Get Latest DB Dump"
              handleSubmit={() =>
                dispatch({
                  type: customerActions.GET_LATEST_DB_DUMP,
                  payload: { fromDate, toDate },
                })
              }
              disabled={!fromDate || !toDate}
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Note: Sync data once per day unless you need the latest records.
            </Typography>
          </Box>
          <hr style={{ margin: "20px 0", borderColor: "lightgray" }} />
        </Grid>

        {/* ── Step 3 – Generate Reconciliation Report ───────────────────── */}
        <Grid item sm={12}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary">
              <b>Step 3</b> – Generate Reconciliation Report
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" mt={2} mb={2}>
              <TextField
                label="From Date"
                type="datetime-local"
                value={toInputValue(reportFromDate)}
                onChange={handleReportFromDateChange}
                sx={{ width: "260px" }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To Date"
                type="datetime-local"
                value={toInputValue(reportToDate)}
                onChange={handleReportToDateChange}
                sx={{ width: "260px" }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
              <FormControl sx={{ width: 210 }} size="small">
                <InputLabel>Select Course</InputLabel>
                <Select
                  value={courseid}
                  label="Select Course"
                  onChange={onChangeCourse}
                >
                  <MenuItem value="">All</MenuItem>
                  {courseList?.all?.filter((course) => course.isactive === "1")
                    .map((course,i) => (
                      <MenuItem key={i} value={course.courseid}>
                        {course.coursename}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <FormControl sx={{ width: 210 }} size="small">
                <InputLabel>Reconciliation Status</InputLabel>
                <Select
                  value={reconciliationStatus}
                  label="Reconciliation Status"
                  onChange={(e) => setReconciliationStatus(e.target.value)}
                >
                  {RECONCILIATION_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ width: 160 }} size="small">
                <InputLabel>Portal Status</InputLabel>
                <Select
                  value={portalStatus}
                  label="Portal Status"
                  onChange={(e) => setPortalStatus(e.target.value)}
                >
                  {PORTAL_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ width: 160 }} size="small">
                <InputLabel>Airpay Status</InputLabel>
                <Select
                  value={airpayStatus}
                  label="Airpay Status"
                  onChange={(e) => setAirpayStatus(e.target.value)}
                >
                  {AIRPAY_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box mt={2}>
              <BluePrimaryButton
                title="Generate Reconciliation Report"
                handleSubmit={handleGenerate}
                disabled={!reportFromDate || !reportToDate}
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Note: Report may take a few seconds to generate.
              </Typography>
            </Box>
          </Box>
          <hr style={{ margin: "20px 0", borderColor: "lightgray" }} />
        </Grid>

        {/* ── Summary Cards + Pie Chart ──────────────────────────────────── */}
        {Object.keys(summary).length > 0 && (
          <Grid item sm={12}>
            <Box display="flex" gap={3} flexWrap="wrap" alignItems="flex-start">
              <Box flex="1" minWidth={300}>
                <SummaryCards summary={summary} />
              </Box>
              <Box sx={{ width: 320 }}>
                <StatusPieChart summary={summary} />
              </Box>
            </Box>
            <hr style={{ margin: "20px 0", borderColor: "lightgray" }} />
          </Grid>
        )}

        {/* ── Report Table ───────────────────────────────────────────────── */}
        <Grid item sm={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              backgroundColor: "whitesmoke",
              padding: "8px",
              borderRadius: "4px",
            }}
          >
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, color: "black", fontSize: "20px", lineHeight: "24px" }}
              >
                Reconciliation Report
              </Typography>
              {pagination?.total > 0 && (
                <Typography variant="caption" color="textSecondary">
                  Total records: {pagination.total}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              color="success"
              onClick={downloadReport}
              disabled={applicationStatusData.length === 0}
            >
              Download CSV
            </Button>
          </Box>

          <ReconcilationReportTable
            columnData={reconciliationColumns}
            tableData={applicationStatusData}
            serverSide={true}
            totalCount={pagination?.total ?? 0}
            page={currentPage - 1}
            rowsPerPage={pageSize}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Grid>
      </Card>

      {/* ── Override Modal ─────────────────────────────────────────────────── */}
      <OverrideModal
        open={overrideOpen}
        row={overrideRow}
        onClose={handleOverrideClose}
        onSubmit={handleOverrideSubmit}
      />
    </Box>
  );
}
